import { useRef } from "react";
import { LeadSection } from "@/components/LeadSection";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Sparkles, Tv, Users, ArrowUpRight } from "lucide-react";
import { DepartureBoard } from "@/components/DepartureBoard";
import { TextMarquee } from "@/components/TextMarquee";
import { PassportStamp } from "@/components/PassportStamp";
import { Reveal, MaskLine, Stagger, StaggerItem } from "@/components/motion";
import { CTA, Eyebrow, Section } from "@/components/kit";
import { Season2Teaser } from "@/components/Season2Teaser";
import { Season2Route } from "@/components/Season2Route";
import { Faq } from "@/components/Faq";
import { useLang } from "@/i18n/LanguageContext";
import { EPISODES, ACTS, IMG, SCHEDULE } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const { t } = useLang();

  return (
    <section ref={ref} className="relative overflow-hidden px-5 sm:px-8 lg:px-12 pt-10 sm:pt-16 pb-20">
      {/* fondos suaves */}
      <div className="pointer-events-none absolute -left-32 top-20 h-96 w-96 rounded-full bg-mint/40 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-40 h-80 w-80 rounded-full bg-sky/30 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Eyebrow>{t("hero.eyebrow")}</Eyebrow>
          </motion.div>

          <h1 className="mt-5 font-display text-6xl sm:text-7xl lg:text-8xl font-extrabold leading-[0.9] tracking-tighter text-ink">
            <MaskLine delay={0.15}>{t("hero.l1")}</MaskLine>
            <MaskLine delay={0.28}>
              <span className="text-coral">{t("hero.l2a")}</span>
              {t("hero.l2b")}
            </MaskLine>
            <MaskLine delay={0.41}>{t("hero.l3")}</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-7 max-w-lg text-lg text-ink/70 leading-relaxed"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <CTA to="/episodios" variant="coral" data-testid="hero-cta-episodios">
              {t("hero.ctaEp")}
            </CTA>
            <CTA to="/anto-bella" variant="outline" icon={false} data-testid="hero-cta-duo">
              {t("hero.ctaDuo")}
            </CTA>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-9 flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-ink/50"
          >
            <Tv className="h-4 w-4 text-coral" />
            {SCHEDULE.main} · {SCHEDULE.region}
          </motion.div>
        </div>

        {/* Ilustración del dúo + tablero de salidas (apilados, sin encimar) */}
        <motion.div style={{ y }} className="relative">
          <motion.img
            style={{ y: yImg }}
            src={IMG.duoHero}
            alt="Ilustración de Anto y Bella listas para viajar"
            data-testid="hero-duo-image"
            className="pointer-events-none mx-auto mb-4 block h-72 w-64 object-contain lg:mb-3 lg:ml-auto lg:mr-0 lg:h-80 lg:w-80"
          />
          <div className="relative">
            <DepartureBoard rows={EPISODES.slice(0, 6)} />
            <div className="absolute -bottom-8 -right-4 z-20">
              <PassportStamp code="A&B" label="TEMP.1" color="#E8C547" rotate={-14} size={104} />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const stats = [
  { icon: MapPin, k: "12", v: "Destinos por el mundo" },
  { icon: Tv, k: "6", v: "Actos por cada episodio" },
  { icon: Users, k: "2", v: "Amigas inseparables" },
  { icon: Sparkles, k: "1ra", v: "Temporada épica" },
];

const StatsBand = () => (
  <Section alt className="!py-16">
    <Stagger className="grid grid-cols-2 gap-6 lg:grid-cols-4">
      {stats.map((s) => (
        <StaggerItem
          key={s.v}
          className="rounded-[2rem] bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        >
          <s.icon className="h-7 w-7 text-coral" />
          <div className="mt-5 font-display text-5xl font-extrabold tracking-tighter text-ink">
            {s.k}
          </div>
          <div className="mt-1 font-mono text-xs uppercase tracking-widest text-ink/50">
            {s.v}
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  </Section>
);

const RoutePreview = () => (
  <Section>
    <div className="flex flex-wrap items-end justify-between gap-6">
      <div>
        <Eyebrow color="#81D4FA">La Ruta</Eyebrow>
        <h2 className="mt-4 max-w-xl font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
          Doce paradas, doce historias. El mundo cabe en una maleta.
        </h2>
      </div>
      <Link
        to="/la-ruta"
        data-testid="home-verruta"
        className="group inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-ink hover:text-coral"
      >
        Ver la ruta completa
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
      </Link>
    </div>

    <div className="mt-12 grid auto-rows-[220px] grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
      {EPISODES.slice(0, 6).map((e, i) => {
        const big = i === 0 || i === 3;
        return (
          <Reveal
            key={e.n}
            delay={i * 0.06}
            className={`group relative overflow-hidden rounded-[2rem] ${
              big ? "col-span-2 row-span-1" : ""
            }`}
          >
            <img
              src={e.img}
              alt={e.city}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/10 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-between p-5">
              <span
                className="w-fit rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-ink"
                style={{ backgroundColor: e.color }}
              >
                {e.n} · {e.code}
              </span>
              <div>
                <p className="font-display text-xl font-bold leading-tight text-white">
                  {e.city}
                </p>
                <p className="font-mono text-[11px] uppercase tracking-widest text-white/70">
                  {e.title}
                </p>
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  </Section>
);

const FormatPreview = () => (
  <Section alt>
    <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
      <div>
        <Eyebrow color="#FF8A80">El Formato</Eyebrow>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
          No hace falta saberlo todo para empezar. Seis actos cada episodio.
        </h2>
        <p className="mt-6 text-lg text-ink/70 leading-relaxed">
          La curiosidad también tiene ritmo. Esta es una serie para viajar con los ojos abiertos. Para encontrar lo extraordinario en una receta de barrio, en una puerta pintada a mano o en la forma en que alguien cuenta su ciudad.
        </p>
        <CTA to="/el-formato" variant="ink" className="mt-8" data-testid="home-verformato">
          Explorar el formato
        </CTA>
      </div>
      <Stagger className="grid gap-3">
        {ACTS.map((a) => (
          <StaggerItem
            key={a.n}
            className="flex items-center gap-4 rounded-[1.5rem] bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-transform hover:translate-x-2 sm:gap-5 sm:p-5"
          >
            <img
              src={a.img}
              alt={a.title}
              loading="lazy"
              className="h-16 w-16 shrink-0 rounded-2xl object-cover sm:h-20 sm:w-20"
            />
            <span className="font-mono text-2xl font-bold text-coral">{a.n}</span>
            <div>
              <p className="font-display text-lg font-bold text-ink">{a.title}</p>
              <p className="text-sm text-ink/60">{a.desc}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </Section>
);

const CastPreview = () => (
  <Section>
    <Reveal className="text-center">
      <Eyebrow color="#E8C547" className="justify-center">
        El dúo
      </Eyebrow>
      <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl sm:text-5xl font-extrabold leading-none tracking-tighter text-ink">
        Opuestas. Y por eso, perfectas juntas.
      </h2>
    </Reveal>

    <div className="mt-14 grid gap-6 md:grid-cols-2">
      {[
        {
          name: "Bella",
          full: "Isabella Arroyo",
          role: "Estructura · Elegancia · Presencia",
          desc: "Actriz mexicana, impecable y disciplinada. Ballet, tap y tenis. Seis meses mayor y la calma que da forma a cada aventura.",
          img: IMG.bellaPortrait,
          color: "#FF8A80",
        },
        {
          name: "Anto",
          full: "Antonella",
          role: "Chispa · Creatividad · Espontaneidad",
          desc: "Diseñadora, chef y gamer. Pinta espectacular y hace las muecas que se vuelven virales. El corazón risueño del dúo.",
          img: IMG.antoPortrait,
          color: "#81D4FA",
        },
      ].map((p, i) => (
        <Reveal
          key={p.name}
          delay={i * 0.1}
          className="group relative overflow-hidden rounded-[2.5rem] bg-mist"
        >
          <div className="grid grid-cols-[1fr_1.2fr] items-center gap-4">
            <div className="relative overflow-hidden">
              <img
                src={p.img}
                alt={p.full}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="py-8 pr-8">
              <p
                className="font-mono text-xs uppercase tracking-widest"
                style={{ color: p.color }}
              >
                {p.role}
              </p>
              <h3 className="mt-2 font-display text-4xl font-extrabold tracking-tighter text-ink">
                {p.name}
              </h3>
              <p className="font-mono text-xs uppercase tracking-widest text-ink/40">
                {p.full}
              </p>
              <p className="mt-4 text-sm text-ink/70 leading-relaxed">{p.desc}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

const JoinBand = () => (
  <section className="px-5 sm:px-8 lg:px-12 py-20">
    <div className="mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-ink px-8 py-16 sm:px-14 sm:py-20 text-center">
      <Reveal>
        <Eyebrow color="#A8E6CF" className="justify-center">
          Únete a la comunidad
        </Eyebrow>
        <h2 className="mx-auto mt-5 max-w-3xl font-display text-4xl sm:text-6xl font-extrabold leading-[0.95] tracking-tighter text-cream">
          ¿Lista para volverte <span className="text-coral">Believer</span>?
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-cream/70 leading-relaxed">
          Ayudales a reparar la maleta como quien prepara una buena conversación: con lo indispensable, enviale mas preguntas que ropa, creale retos desde tu casa.
          Y hasta podrias viajar con ellas.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <CTA to="/believers" variant="mint" data-testid="home-believers">
            Ser Believer
          </CTA>
          <CTA to="/vip" variant="coral" data-testid="home-vip">
            Área VIP
          </CTA>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <TextMarquee
        items={["Curiosidad", "Amistad", "Aventura", "Cero pose", "Regalos", "VideoChats", "Sellos Digitales", "TV Online"]}
      />
      <StatsBand />
      <RoutePreview />
      <FormatPreview />
      <CastPreview />
      <Season2Teaser alt />
      <Faq alt />
      <JoinBand />
      <LeadSection path="/" alt />
    </>
  );
}
